import { CellValueChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { MdOutlineContentCopy } from "react-icons/md";
import { useSplashMessage } from "src/hook/useSplashMessage";

import { useAuthUserContext } from "../../context/AuthUser";
import { useDialog } from "../../context/DialogProvider";
import useApi from "../../hook/useApi";
import { OrdersPostRequest } from "../../model/api/request/OrdersPostRequest";
import { OrdersPostRequestSub } from "../../model/api/request/OrdersPostRequestSub";
import { OrderGetResponse } from "../../model/api/response/OrderGetResponse";
import { OrderGetResponseSub } from "../../model/api/response/OrderGetResponseSub";
import { ProductGetResponse } from "../../model/api/response/ProductGetResponse";
import { ProductsGetResponse } from "../../model/api/response/ProductsGetResponse";
import { QuotationGetResponse } from "../../model/api/response/QuotationGetResponse";
import {
  OrderDetailFormRowValues,
  OrderDetailFormValues,
  OrderDetailHandles,
  OrderDetailSaveButtonName,
  OrderDetailShowParam,
  OrderParam,
} from "../../model/form/OrderDetailForm";
import { defaultColDef } from "../../util/AgGridUtil";
import {
  OrderFactor,
  OrderStatus,
  ProductCodeConst,
} from "../../util/Constant";
import { calculateTax } from "../../util/TaxUtil";
import { formatDate, isEnglish } from "../../util/ecUtil";
import S3Uploader, { S3UploaderHandles } from "../common/S3Uploader";
import ProductListModal, {
  ProductListModalHandles,
} from "../setting/ProductListModal";
import OrderDetailDelivery from "./OrderDetailDelivery";
import OrderDetailDiscountRate from "./OrderDetailDiscountRate";
import OrderDetailFooter from "./OrderDetailFooter";
import { buildOrderDetailColumnDefs } from "./OrderDetailGrid";
import OrderDetailHeader, {
  OrderDetailChangeEvent,
  OrderDetailType,
} from "./OrderDetailHeader";
import { createOrderDetailPermission } from "./OrderDetailPermission";
import OrderDetailPrice from "./OrderDetailPrice";
import {
  calculateReplyPriceCore,
  canAddExpensesRow,
  copyDetailsToClipboard,
  isEmptyOrderRow,
  useDetailRowDrag,
} from "./OrderQuotationUtil";

// 呼び出し元との互換のため型を再公開する
export type { OrderDetailHandles, OrderParam };

type Props = {
  onClickSaveButton?: (
    buttonName: OrderDetailSaveButtonName,
    retOrderParm: OrderParam
  ) => void;
  fromQuotationDetail?: boolean;
  quotationOrderHistoryFlg?: boolean;
};

// ヘッダ部見出しの幅
const itemTitleStyle = { width: "11rem" };

// エリアごとのCSSクラス
const areaClass = "p-1 bg-white b-detail-items";
const textAreaClass =
  "form-control textarea b-input-text-square b-input-comment-height";
/** 注文詳細コンポーネント */
const OrderDetail = forwardRef<OrderDetailHandles, Props>((props, ref) => {
  // ダイアログ使用宣言
  const showDialog = useDialog();
  //スプラッシュメッセージ使用宣言
  const splashMessage = useSplashMessage();
  // API使用宣言
  const api = useApi();
  // ユーザ情報
  const auth = useAuthUserContext();
  // 翻訳
  const { t: tc } = useTranslation("OrderDetail");

  // state宣言
  const [orderNo, setOrderNo] = useState<number | undefined>();
  const [show, setShow] = useState(false);
  const [tempSubtotalPrice, setTempSubtotalPrice] = useState(0); // 仮注文金額
  const [shippingAllFlg, setShippingAllFlg] = useState(false); // 全出荷ボタンフラグ
  const [initialData, setInitialData] = useState<OrderDetailFormValues>(); // 画面表示時のデータ
  const [initialDetails, setInitialDetails] = useState<
    OrderDetailFormRowValues[]
  >([]); // 画面表示時の明細データ

  //クリップボードにコピー関数
  const copyToClipboard = () =>
    copyDetailsToClipboard(
      inpDetails,
      [
        "No",
        "品目コード",
        "品名",
        "型式",
        "単価",
        "数量",
        "単位",
        "小計",
        "明細備考",
        "単価(BEMAC回答)",
        "数量(BEMAC回答)",
        "小計(BEMAC回答)",
      ],
      (inpData) => [
        inpData.orderDetailNo ?? "",
        inpData.productCode ?? "",
        isEnglish()
          ? inpData.productGroupNameEn
          : inpData.productGroupName ?? "",
        inpData.spec ?? "",
        inpData.unitCost ?? "",
        inpData.quantity ?? "",
        inpData.unit ?? "",
        inpData.detailSubTotal ?? "",
        inpData.note ?? "",
        inpData.replyUnitCost ?? "",
        inpData.replyQuantity ?? "",
        inpData.replyDetailSubTotal ?? "",
      ]
    );

  // 入力項目
  const [inpOrder, setInpOrder] = useState<OrderDetailFormValues>(
    generateDraft()
  );
  // 入力項目：明細
  const [inpDetails, setInpDetails] = useState<OrderDetailFormRowValues[]>([]);

  // ユーザ入力有無
  const [formChanged, setFormChanged] = useState<boolean>(false);
  // 読み取り専用フラグ
  const [readOnly, setReadOnly] = useState(false);

  // 参照
  const gridRef = useRef<AgGridReact>(null); // 明細
  const productListModalRef = useRef<ProductListModalHandles>(null); // 商品一覧
  const uploaderCustomerRef = useRef<S3UploaderHandles>(null); // ファイル添付（顧客）
  const uploaderFreeMemo = useRef<S3UploaderHandles>(null); // ファイル添付（フリーメモ）
  const uploaderBemacRef = useRef<S3UploaderHandles>(null); // ファイル添付（BEMAC）
  const uploaderShippingRef = useRef<S3UploaderHandles>(null); // ファイル添付（送り状）
  const [currentRegularPriceFlg, setCurrentRegularPriceFlg] =
    useState<boolean>(false); // 定価フラグ

  // 表示・編集可否判定（ステータス×ユーザ種別）
  const permission = createOrderDetailPermission({
    inpOrder,
    inpDetails,
    readOnly,
    auth,
    fromQuotationDetail: props.fromQuotationDetail,
    getCancelTotalPrice,
  });
  const {
    editableFileUpload,
    editableShippingFileUpload,
    editableUserComment,
    showsUserCommentCancel,
    showsUserCommentDeniedChange,
    editableBemacReplyComment,
    editableBemacFreeComment,
    editableBemacShippingComment,
    canAddNewRow,
    canChangeComplete,
    isGeneralEngineer,
    showsTempPrice,
    editableR3Values,
    visibleReplyDeliveryDate,
    editableReplyDeliveryDate,
    showsShippingColumns,
    showsR3Values,
  } = permission;

  function generateDraft(): OrderDetailFormValues {
    return {
      quotationNo: undefined,
      statusClassValue: OrderStatus.DRAFT,
      orderClassValue: OrderFactor.EC_PARTS,
      companyCode: auth.user().companyCode,
      companyName: auth.user().companyName,
      imoNo: "",
      vesselName: "",
      vesselBuilder: "",
      yardNo: "",
      requestDeliveryDate: "",
      customerOrderNo1: "",
      customerOrderNo2: "",
      requestReplyDeadline: "",
      deliveryName: "",
      deliveryPostalCode: "",
      deliveryAddress: "",
      deliveryTel: "",
      dispatchFlg: false,
      dispatchPlace: "",
      dispatchDate: "",
      orderUserId: auth.user().userId,
      orderUserName: auth.user().userName,
      orderComment: "",
      orderDateTime: "",
      replyUserId: auth.user().bemacFlg ? auth.user().userId : undefined,
      replyUserName: auth.user().bemacFlg ? auth.user().userName : undefined,
      replyComment: "",
      replyDateTime: "",
      replyDeliveryDate: "",
      replyInstallmentDeliveryFlg: false,
      replySubtotalPrice: undefined,
      replyDeliveryPrice: undefined,
      replyDiscountPrice: 0,
      replyTax: 0,
      replyTaxPrice: undefined,
      replyTotalPrice: undefined,
      discountRate: 0,
      checkUserId: undefined,
      checkDateTime: "",
      r3Wbs: "",
      r3ShippingDate: "",
      r3Comment: "",
      updateDateTime: "",
      shippedComment: "",
      quotationDateTime: undefined,
      quotationComment: undefined,
      quotationCheckUserId: undefined,
      quotationCheckUserName: undefined,
      dockName: "",
      dockScheduleStart: "",
      dockScheduleEnd: "",
      s3Prefix: generateS3Prefix(),
      isChangeOrder: false,
      isRequestChangeOrder: false,
      rejectUserId: undefined,
      rejectDateTime: undefined,
      problemProduct: undefined,
      problemDetail: undefined,
      compQuotationFlg: false,
      isReject: false,
      isCompTempSave: false,
      bemacFreeComment: "",
      customerQuotationNo1: undefined,
      customerQuotationNo2: undefined,
    };
  }

  // S3に登録するオブジェクトキーのプリフィクスを採番
  function generateS3Prefix() {
    const datetime = formatDate(new Date(), "YYYYMMDDHHmmss");
    return `order/${datetime}-${auth.user().userId}/`;
  }

  // 公開する関数の実装
  useImperativeHandle(ref, () => ({
    show(param: OrderDetailShowParam) {
      // 初期化
      setFormChanged(false);
      setOrderNo(param.orderNo);
      setShippingAllFlg(false);
      if (param.orderNo) {
        // 注文番号が設定されていたらAPIから初期化する
        api
          .get<OrderGetResponse>(`/api/v1/orders/${param.orderNo}`)
          .then((response) => {
            const newOrder = mapOrderGetResponseToFormValues(response.data);
            const detailRows = mapOrderGetResponseSubToFormRowValues(
              response.data.orderDetail,
              true
            );

            // 見積番号が設定されていたら見積の情報を検索する
            if (newOrder.quotationNo) {
              api
                .get<QuotationGetResponse>(
                  `/api/v1/quotations/${newOrder.quotationNo}`
                )
                .then((responseQuotation) => {
                  newOrder.customerQuotationNo1 =
                    responseQuotation.data.customerQuotationNo1 === null
                      ? undefined
                      : responseQuotation.data.customerQuotationNo1;
                  newOrder.customerQuotationNo2 =
                    responseQuotation.data.customerQuotationNo2 === null
                      ? undefined
                      : responseQuotation.data.customerQuotationNo2;
                  saveInitialDataAndShow(newOrder, detailRows);

                  // BEMACユーザかつ注文中の場合一時保存のデータが存在する(注文変更トランを参照)可能性があるため、初期データを注文データで更新する
                  updateScreenByDraftOrder(newOrder, param.orderNo);
                })
                .catch((error) => showDialog({ error }));
            } else {
              saveInitialDataAndShow(newOrder, detailRows);
              // BEMACユーザかつ注文中の場合一時保存のデータが存在する(注文変更トランを参照)可能性があるため、初期データを注文データで更新する
              updateScreenByDraftOrder(newOrder, param.orderNo);
            }
          })
          .catch((error) => showDialog({ error }));
      } else if (param.quotationData) {
        // 見積データが設定されていたらそのまま表示する
        const newOrder = mapOrderGetResponseToFormValues(param.quotationData);
        const detailRows = mapOrderGetResponseSubToFormRowValues(
          param.quotationData.orderDetail,
          true
        );

        if (param.customerQuotationNo1) {
          newOrder.customerQuotationNo1 = param.customerQuotationNo1;
        }

        if (param.customerQuotationNo2) {
          newOrder.customerQuotationNo2 = param.customerQuotationNo2;
        }

        saveInitialDataAndShow(newOrder, detailRows);
      } else {
        // 新規作成の場合
        const newOrder = generateDraft();
        if (param.initialImoNo) {
          newOrder.imoNo = param.initialImoNo;
        }
        if (param.initialVesselName) {
          newOrder.vesselName = param.initialVesselName;
        }
        if (param.initialVesselBuilder) {
          newOrder.vesselBuilder = param.initialVesselBuilder;
        }
        if (param.initialYardNo) {
          newOrder.yardNo = param.initialYardNo;
        }
        const detailRows: OrderDetailFormRowValues[] = [];
        // stateに保存してモーダルを表示状態にする
        saveInitialDataAndShow(newOrder, detailRows);
      }
    },
  }));

  function updateScreenByDraftOrder(
    newOrder: OrderDetailFormValues,
    orderNo: number | undefined
  ) {
    if (
      auth.user().bemacFlg &&
      newOrder.statusClassValue === OrderStatus.ORDERED
    ) {
      api
        .get<OrderGetResponse>(
          `/api/v1/orders/${orderNo}?notGetChangeDataFlg=true`
        )
        .then((response) => {
          const newOrderInitial = mapOrderGetResponseToFormValues(
            response.data
          );
          const detailRowsInitial = mapOrderGetResponseSubToFormRowValues(
            response.data.orderDetail,
            true
          );
          saveInitialDataAndShow(newOrderInitial, detailRowsInitial, true);
        })
        .catch((error) => showDialog({ error }));
    }
  }

  function mapOrderGetResponseToFormValues(src: OrderGetResponse) {
    const mapped: OrderDetailFormValues = {
      quotationNo: src.quotationNo ?? undefined,
      statusClassValue: src.statusClassValue,
      orderClassValue: src.orderClassValue ?? undefined,
      companyCode: src.companyCode,
      companyName: src.companyName ?? undefined,
      imoNo: src.imoNo ?? undefined,
      vesselName: src.vesselName ?? undefined,
      vesselBuilder: src.vesselBuilder ?? undefined,
      yardNo: src.yardNo ?? undefined,
      requestDeliveryDate: src.requestDeliveryDate ?? undefined,
      customerOrderNo1: src.customerOrderNo1 ?? undefined,
      customerOrderNo2: src.customerOrderNo2 ?? undefined,
      requestReplyDeadline: src.requestReplyDeadline ?? undefined,
      deliveryName: src.deliveryName,
      deliveryPostalCode: src.deliveryPostalCode,
      deliveryAddress: src.deliveryAddress,
      deliveryTel: src.deliveryTel,
      dispatchFlg: src.dispatchFlg,
      dispatchPlace: src.dispatchPlace ?? undefined,
      dispatchDate: src.dispatchDate ?? undefined,
      orderUserId: src.orderUserId,
      orderUserName: src.orderUserName,
      orderComment: src.orderComment ?? undefined,
      orderDateTime: src.orderDateTime ?? undefined,
      replyUserId: src.replyUserId ?? undefined,
      replyUserName: src.replyUserName ?? undefined,
      replyComment: src.replyComment ?? undefined,
      replyDateTime: src.replyDateTime ?? undefined,
      replyDeliveryDate: src.replyDeliveryDate ?? undefined,
      replyInstallmentDeliveryFlg: src.replyInstallmentDeliveryFlg,
      replySubtotalPrice: src.replySubtotalPrice ?? undefined,
      replyDeliveryPrice: src.replyDeliveryPrice ?? undefined,
      replyDiscountPrice: src.replyDiscountPrice ?? undefined,
      replyTax: src.replyTax ?? undefined,
      replyTaxPrice: src.replyTaxPrice ?? undefined,
      replyTotalPrice: src.replyTotalPrice ?? undefined,
      discountRate: src.discountRate ?? undefined,
      checkUserId: src.checkUserId ?? undefined,
      checkDateTime: src.checkDateTime ?? undefined,
      r3Wbs: src.r3Wbs ?? undefined,
      r3ShippingDate: src.r3ShippingDate ?? undefined,
      r3Comment: src.r3Comment ?? undefined,
      // s3Prefixが付いてなければ付けなおす
      s3Prefix: src.s3Prefix ?? generateS3Prefix(),
      updateDateTime: src.updateDateTime ?? undefined,
      shippedComment: src.shippedComment ?? undefined,
      quotationDateTime: src.quotationDateTime ?? undefined,
      quotationComment: src.quotationComment ?? undefined,
      quotationCheckUserId: src.quotationCheckUserId ?? undefined,
      quotationCheckUserName: src.quotationCheckUserName ?? undefined,
      dockName: src.dockName ?? undefined,
      dockScheduleStart: src.dockScheduleStart ?? undefined,
      dockScheduleEnd: src.dockScheduleEnd ?? undefined,
      isChangeOrder: false,
      isRequestChangeOrder: false,
      rejectUserId: src.rejectUserId ?? undefined,
      rejectDateTime: src.rejectDateTime ?? undefined,
      problemProduct: src.problemProduct ?? undefined,
      problemDetail: src.problemDetail ?? undefined,
      compQuotationFlg: src.compQuotationFlg,
      isReject: false,
      isCompTempSave: src.isCompTempSave,
      bemacFreeComment: src.bemacFreeComment ?? undefined,
      customerQuotationNo1: undefined,
      customerQuotationNo2: undefined,
    };
    return mapped;
  }

  function mapOrderGetResponseSubToFormRowValues(
    src: OrderGetResponseSub[],
    isDb: boolean
  ) {
    const mappedRows = src.map((it, index) => {
      const newRow: OrderDetailFormRowValues = {
        key: index,
        isDbRow: !auth.user().bemacFlg
          ? isDb
          : inpOrder.statusClassValue === OrderStatus.COMPLETED &&
            it.confirmFlg === false
          ? false
          : isDb,
        orderDetailNo: it.orderDetailNo,
        productCode: it.productCode,
        productGroupName: it.productGroupName ?? undefined,
        productGroupNameEn: it.productGroupNameEn ?? undefined,
        spec: it.spec ?? undefined,
        unit: it.unit ?? undefined,
        note: it.note ?? undefined,
        quantity: it.quantity,
        unitCost: it.unitCost ?? undefined,
        detailSubTotal: it.detailSubTotal ?? undefined,
        replyQuantity: it.replyQuantity ?? undefined,
        replyUnitCost: it.replyUnitCost ?? undefined,
        replyDetailSubTotal: it.replyDetailSubTotal ?? undefined,
        replyDeliveryDate: it.replyDeliveryDate ?? undefined,
        shippingQuantity: it.shippingQuantity ?? undefined,
        confirmFlg: it.confirmFlg,
        confirmUserId: it.confirmUserId ?? undefined,
        confirmDateTime: it.confirmDateTime ?? undefined,
        cancelOrderQuantity: it.cancelOrderQuantity ?? undefined,
        cancelUserId: it.cancelUserId ?? undefined,
        cancelDateTime: it.cancelDateTime ?? undefined,
        r3WbsLv2: it.r3WbsLv2 ?? undefined,
        r3OrderNo: it.r3OrderNo ?? undefined,
        r3ShippingDate: it.r3ShippingDate ?? undefined,
        referenceDetailNo: it.referenceDetailNo ?? undefined,
        cancelSubTotal: undefined,
        isExpenses: it.isExpenses,
        isDeliveryCost: it.isDeliveryCost,
        existFlg: true,
      };
      calculateRowSubTotal(newRow); // 小計計算
      return newRow;
    });
    return mappedRows;
  }

  // stateに保存してモーダルを表示状態にする
  function saveInitialDataAndShow(
    order: OrderDetailFormValues,
    detailRows: OrderDetailFormRowValues[],
    updateInitialOnly: boolean = false
  ) {
    if (
      !order.replyUserId &&
      !auth.isEngineer() &&
      auth.user().bemacFlg &&
      order.statusClassValue === OrderStatus.ORDERED
    ) {
      // 注文中はBEMACログインユーザを表示させる
      order.replyUserId = auth.user().userId;
      order.replyUserName = auth.user().userName;
    }
    // stateに保存
    if (!updateInitialOnly) {
      setInpOrder(order);
    }

    // 行追加可能時は新規行を追加
    // 技師以外
    // BEMAC・LCM部品注文以外で注文中、部品手配中、完了(LCM部品注文ば部品交換リストと紐づける為必ず商品一覧から選択する)
    // 顧客・作成中は追加可能
    if (
      !auth.isEngineer() &&
      ((auth.user().bemacFlg &&
        order.orderClassValue !== OrderFactor.LCM_PARTS &&
        (order.statusClassValue === OrderStatus.ORDERED ||
          OrderStatus.ARRANGING === order.statusClassValue ||
          OrderStatus.COMPLETED === order.statusClassValue)) ||
        (!auth.user().bemacFlg && order.statusClassValue === OrderStatus.DRAFT))
    ) {
      //Bemacユーザで注文中かつ技師派遣フラグONで経費行が存在しない場合、空白行と経費行も追加する
      if (
        auth.user().bemacFlg &&
        OrderStatus.ORDERED === order.statusClassValue &&
        order.dispatchFlg &&
        canAddExpensesRow(detailRows)
      ) {
        //「---」行、経費行用の空白行、新規入力用の空白行を追加
        addNewRow(detailRows, [ProductCodeConst.BLANK, ""]);
        if (!updateInitialOnly) {
          setInpDetails(detailRows);
        }

        //経費行を追加
        addExpensesRow(updateInitialOnly ? detailRows : undefined, true);
      } else {
        addNewRow(detailRows);
        if (!updateInitialOnly) {
          setInpDetails(detailRows);
        }
      }
    } else {
      if (!updateInitialOnly) {
        setInpDetails(detailRows);
      }
    }

    // 金額再計算
    if (!updateInitialOnly) {
      calculateTempPrice(detailRows);
      calculateReplyPrice(detailRows);

      // 読み取り専用モードの設定
      setReadOnlyMode(
        order.statusClassValue,
        order.companyCode,
        order.orderUserId
      );
    }

    // 初期データを退避
    setInitialData({ ...order });
    const copiedDetails = detailRows.map((it) => {
      return { ...it };
    });
    setInitialDetails(copiedDetails);

    // 表示
    setShow(true);
  }

  // 読み取り専用モードの設定
  function setReadOnlyMode(
    statusClassValue: string,
    companyCode: string,
    orderUserId: number | undefined
  ) {
    // 見積詳細からの表示の場合は読み取り専用
    if (props.fromQuotationDetail) {
      setReadOnly(true);
      return;
    }

    // 技師は読み取り専用
    if (auth.isEngineer()) {
      setReadOnly(true);
      return;
    }

    switch (statusClassValue) {
      case OrderStatus.STOPPED_SHIPPING: // 取消
      case OrderStatus.CHANGED: // 変更
      case OrderStatus.DENIED_CHANGE: // 停止
        // 編集不可
        setReadOnly(true);
        return;
    }

    if (
      // 顧客ユーザかつ取消依頼中、完了は編集不可
      !auth.user().bemacFlg &&
      (statusClassValue === OrderStatus.REQUESTED_CANCEL ||
        statusClassValue === OrderStatus.COMPLETED)
    ) {
      setReadOnly(true);
      return;
    }

    // 顧客の場合は自身が依頼者でなければ読み取り専用
    if (
      !auth.user().bemacFlg &&
      orderUserId &&
      auth.user().userId !== orderUserId
    ) {
      setReadOnly(true);
      return;
    }

    // その他は編集可
    setReadOnly(false);
  }

  // モーダル表示ハンドラ
  const handleShow = () => {
    // アップローダーの初期化
    uploaderCustomerRef.current?.init(`${inpOrder.s3Prefix}customer/`, false);
    uploaderBemacRef.current?.init(`${inpOrder.s3Prefix}bemac/`, false);
    uploaderFreeMemo.current?.init(`${inpOrder.s3Prefix}free-memo/`, false);
    uploaderShippingRef.current?.init(`${inpOrder.s3Prefix}shipping/`, false);
  };

  // モーダルクローズハンドラ
  const handleClose = () => {
    if (formChanged) {
      showDialog({
        id: "I001",
        confirm: true,
        callback(isOk) {
          if (isOk) {
            setShippingAllFlg(false);
            setInpDetails([]);
            setShow(false);
            setCurrentRegularPriceFlg(false);
          }
        },
      });
    } else {
      setShippingAllFlg(false);
      setInpDetails([]);
      setShow(false);
      setCurrentRegularPriceFlg(false);
    }
  };

  // 保存系ボタン
  function handleClickActionButton(name: OrderDetailSaveButtonName) {
    // 明細の編集を停止
    gridRef.current?.api.stopEditing();

    // 見積詳細から開かれた場合
    if (props.fromQuotationDetail) {
      showDialog({
        id: "I014",
        confirm: true,
        callback: (isOk) => {
          if (isOk) {
            // 見積注文時はファイル登録を実施
            // 添付ファイルを処理
            Promise.all([
              uploaderCustomerRef.current!.save(),
              uploaderBemacRef.current!.save(),
              uploaderFreeMemo.current?.save(),
              uploaderShippingRef.current?.save(),
            ]).then(() => {
              afterSaved(name);
            });
          }
        },
      });
      return;
    }

    // 注文承認、更新、出荷後キャンセルの場合は変更チェックの場合は変更チェック
    if (
      (name === "注文承認" || name === "更新" || name === "出荷後キャンセル") &&
      !validateApprove()
    ) {
      // 変更があったらエラー
      if (
        name === "注文承認" ||
        name === "更新" ||
        (!inpOrder.dispatchFlg && name === "出荷後キャンセル")
      ) {
        showDialog({ id: "E070" });
        return;
      } else {
        showDialog({ id: "E086" });
        return;
      }
    }

    if (name === "発注" && inpOrder.requestReplyDeadline) {
      const currentDateTime = parseInt(formatDate(new Date(), "YYYYMMDD"));
      const requestReplyDeadline: number = parseInt(
        inpOrder.requestReplyDeadline?.replaceAll(/-/g, "")
      );
      if (requestReplyDeadline < currentDateTime + 2) {
        // 確認ダイアログを表示
        showDialog({
          id: "E033",
          args: [tc("発注")],
          confirm: true,
          callback(isOk) {
            if (isOk) {
              saveData(name);
            }
          },
        });
      } else {
        showConfirmMsg(name);
      }
    } else if (
      name === "注文承認" &&
      (Number(inpOrder.replyDiscountPrice) === 0 ||
        Number(inpOrder.replyTax) === 0)
    ) {
      // 割引額もしくは消費税額が0のままだったらエラー
      showDialog({
        id: "E085",
        args: ["注文承認"],
        confirm: true,
        callback(isOk) {
          if (isOk) {
            showConfirmMsg(name);
          }
        },
      });
    } else {
      showConfirmMsg(name);
    }
  }

  function showConfirmMsg(name: OrderDetailSaveButtonName) {
    // 確認メッセージ
    const messageId = confirmMessageId(name);
    if (messageId) {
      showDialog({
        id: messageId,
        confirm: true,
        callback: (isOk) => {
          if (isOk) {
            if (!auth.user().bemacFlg && name === "注文変更") {
              // モード切替
              setInpOrder((prev) => ({
                ...prev,
                isChangeOrder: true,
                statusClassValue: OrderStatus.DRAFT,
                orderDateTime: undefined,
              }));
              // 回答から依頼に値を移して明細を組み立てる
              const newDetails = inpDetails.map((detail) => {
                return {
                  ...detail,
                  quantity: detail.replyQuantity,
                  unitCost: detail.replyUnitCost,
                  detailSubTotal: detail.replyDetailSubTotal,
                };
              });
              setInpDetails(newDetails);

              calculateTempPrice(newDetails);
              calculateReplyPrice(newDetails);
            } else {
              saveData(name);
            }
          }
        },
      });
    } else {
      // メッセージなしで保存
      saveData(name);
    }
  }

  function confirmMessageId(name: OrderDetailSaveButtonName) {
    const messageIdMap: {
      name: OrderDetailSaveButtonName;
      messageId: string;
    }[] = [
      { name: "発注", messageId: "I022" },
      { name: "注文取消依頼", messageId: "I023" },
      { name: "注文取消承認", messageId: "I065" },
      { name: "注文停止", messageId: "I063" },
      { name: "注文変更依頼", messageId: "I024" },
      { name: "完工見積", messageId: "I070" },
      { name: "注文承認", messageId: "I025" },
      { name: "注文変更", messageId: "I027" },
      { name: "更新", messageId: "I026" },
      { name: "出荷後キャンセル", messageId: "I029" },
      { name: "差戻", messageId: "I068" },
    ];
    return messageIdMap.find((it) => it.name === name)?.messageId;
  }

  function savedMessageId(name: OrderDetailSaveButtonName) {
    const messageIdMap: {
      name: OrderDetailSaveButtonName;
      messageId: string;
    }[] = [
      { name: "一時保存", messageId: "I045" },
      { name: "注文中一時保存", messageId: "I045" },
      { name: "発注", messageId: "I047" },
      { name: "注文取消依頼", messageId: "I066" },
      { name: "注文取消承認", messageId: "I048" },
      { name: "注文停止", messageId: "I064" },
      { name: "注文変更依頼", messageId: "I049" },
      { name: "完工見積", messageId: "I071" },
      { name: "完工見積一時保存", messageId: "I045" },
      { name: "注文承認", messageId: "I050" },
      { name: "注文変更", messageId: "I052" },
      { name: "更新", messageId: "I051" },
      { name: "出荷後キャンセル", messageId: "I054" },
      { name: "差戻", messageId: "I069" },
    ];
    return messageIdMap.find((it) => it.name === name)?.messageId;
  }

  // データ保存
  function saveData(buttonName: OrderDetailSaveButtonName) {
    // 添付ファイルを処理
    const uploadPromises = Promise.all([
      uploaderCustomerRef.current!.save(),
      uploaderBemacRef.current!.save(),
      uploaderFreeMemo.current?.save(),
      uploaderShippingRef.current?.save(),
    ]).then(() => {
      // 添付ファイルの処理が正常終了したら注文登録

      // 見積明細
      // 最終行の新規行を除いてリストを再生成
      const newFormRows = [...inpDetails]
        .filter((it) => !isEmptyOrderRow(it))
        .map((it) => {
          const formRow: OrderDetailFormRowValues = { ...it };
          // BEMACは依頼数量を入力できないので0で補完する
          if (auth.user().bemacFlg && !formRow.quantity) {
            formRow.quantity = 0;
            formRow.detailSubTotal = 0;
          }

          //品目コード=「---」の時は単価、数量を0で補完する
          if (
            formRow.productCode &&
            formRow.productCode === ProductCodeConst.BLANK
          ) {
            formRow.quantity = 0;
            formRow.unitCost = 0;
            formRow.replyQuantity = 0;
            formRow.replyUnitCost = 0;
          }

          // 確定出荷数量を編集
          if (buttonName === "発注" && inpOrder.compQuotationFlg) {
            formRow.cancelOrderQuantity = 0;
            formRow.confirmFlg = true;
          }
          const replyQuantity = formRow.replyQuantity ?? 0;
          const cancelOrderQuantity = formRow.cancelOrderQuantity ?? 0;
          formRow.shippingQuantity = formRow.confirmFlg
            ? replyQuantity - cancelOrderQuantity
            : 0;

          // 発注の場合は回答列に初期値セット
          if (buttonName === "発注") {
            if (formRow.productCode !== ProductCodeConst.EXPENSES) {
              formRow.replyQuantity = formRow.quantity;
              formRow.replyUnitCost = formRow.unitCost;
            }
            calculateRowSubTotal(formRow);
          }

          return formRow;
        });
      const requestSubs = newFormRows.map((it) => {
        // リクエスト明細に変換
        const newRow: OrdersPostRequestSub = {
          ...it,
        };
        return newRow;
      });

      const request: OrdersPostRequest = {
        ...inpOrder,
        orderNo: orderNo,
        statusClassValue: getNextStatus(buttonName),
        ordersPostRequestSub: requestSubs,
      };
      // 発注の場合は金額計算
      if (buttonName === "発注") {
        const price = calculateReplyPriceCore(
          newFormRows,
          inpOrder.replyDiscountPrice,
          inpOrder.replyTax
        );
        request.replySubtotalPrice = price.subtotalPrice;
        request.replyTaxPrice = price.taxPrice;
        request.replyTotalPrice = price.totalPrice;
      }

      if (buttonName === "完工見積") {
        request.compQuotationFlg = true;
      }

      if (
        buttonName === "注文中一時保存" ||
        buttonName === "注文変更依頼" ||
        buttonName === "完工見積" ||
        buttonName === "完工見積一時保存"
      ) {
        // 注文変更依頼フラグON
        request.isRequestChangeOrder = true;
      }

      // 差戻の場合は差戻実施フラグをtrueにする
      if (buttonName === "差戻") {
        request.isReject = true;
      }
      // 注文登録API実行
      return api
        .post(`/api/v1/orders`, request)
        .then((response) => {
          const messageId = savedMessageId(buttonName);
          if (messageId) {
            splashMessage.show(messageId);
          }
          afterSaved(buttonName, response.data.orderNo);
        })
        .catch((error) => {
          if (error.response.status === 404) {
            showDialog({ id: "E027" });
          } else {
            showDialog({ error });
            // 商品マスタの存在チェック
            checkProductCodes();
          }
        });
    });
    return uploadPromises;
  }

  /** 商品マスタの存在チェック */
  async function checkProductCodes() {
    // 注文明細から品目コードを重複なく取得する(空文字等は除く)
    const productCodes = Array.from(
      new Set(
        inpDetails
          .filter(
            (it) =>
              it.productCode.length > 0 &&
              it.productCode !== ProductCodeConst.BLANK
          )
          .map((it) => it.productCode)
      )
    );

    // 商品マスタから商品情報を取得する
    const notExistProductCodes: string[] = [];
    const promises = productCodes.map((productCode) =>
      api
        .get<ProductGetResponse>(`/api/v1/products/${productCode}`)
        .then(() => {})
        .catch((error) => {
          if (error.response.status === 404) {
            notExistProductCodes.push(productCode);
          } else {
            throw error;
          }
        })
    );
    await Promise.all(promises);
    // 注文明細で品目マスタに存在明細の存在フラグを落とす
    setInpDetails((prev) => {
      const rows = prev.map((row) => {
        row.existFlg = !notExistProductCodes.includes(row.productCode ?? "");
        return row;
      });
      return rows;
    });
  }

  function afterSaved(buttonName: OrderDetailSaveButtonName, orderNo?: number) {
    setFormChanged(false);

    if (buttonName === "一時保存" || buttonName === "注文中一時保存") {
      setOrderNo(orderNo);

      api
        .get<OrderGetResponse>(`/api/v1/orders/${orderNo}`)
        .then((response) => {
          const newOrder = mapOrderGetResponseToFormValues(response.data);
          const detailRows = mapOrderGetResponseSubToFormRowValues(
            response.data.orderDetail,
            true
          );
          saveInitialDataAndShow(newOrder, detailRows);

          // BEMACユーザかつ注文中の場合一時保存のデータが存在する(注文変更トランを参照)可能性があるため、初期データを注文データで更新する
          if (
            auth.user().bemacFlg &&
            newOrder.statusClassValue === OrderStatus.ORDERED
          ) {
            api
              .get<OrderGetResponse>(
                `/api/v1/orders/${orderNo}?notGetChangeDataFlg=true`
              )
              .then((response) => {
                const newOrderInitial = mapOrderGetResponseToFormValues(
                  response.data
                );
                const detailRowsInitial = mapOrderGetResponseSubToFormRowValues(
                  response.data.orderDetail,
                  true
                );
                saveInitialDataAndShow(
                  newOrderInitial,
                  detailRowsInitial,
                  true
                );
              })
              .catch((error) => showDialog({ error }));
          }
        })
        .catch((error) => showDialog({ error }));
    } else {
      // 一時保存以外は画面を閉じる
      setShow(false);
    }
    if (props.onClickSaveButton) {
      const retOrderParam: OrderParam = {
        customerOrderNo1:
          inpOrder.customerOrderNo1 === ""
            ? undefined
            : inpOrder.customerOrderNo1,
        customerOrderNo2:
          inpOrder.customerOrderNo2 === ""
            ? undefined
            : inpOrder.customerOrderNo2,
        customerQuotationNo1:
          inpOrder.customerQuotationNo1 === ""
            ? undefined
            : inpOrder.customerQuotationNo1,
        customerQuotationNo2:
          inpOrder.customerQuotationNo2 === ""
            ? undefined
            : inpOrder.customerQuotationNo2,
      };

      props.onClickSaveButton(buttonName, retOrderParam);
    }
  }

  function getNextStatus(name: OrderDetailSaveButtonName) {
    switch (name) {
      case "発注":
        return OrderStatus.ORDERED;
      case "注文取消依頼":
        return OrderStatus.REQUESTED_CANCEL;
      case "注文取消承認":
        return OrderStatus.STOPPED_SHIPPING;
      case "注文停止":
        return OrderStatus.DENIED_CHANGE;
      case "注文変更依頼":
      case "完工見積":
        return OrderStatus.REQUESTED_CHANGE;
      case "注文承認":
        return OrderStatus.ARRANGING;
      case "注文変更":
        return OrderStatus.CHANGED;
      case "更新":
        if (canChangeComplete()) {
          // 全明細出荷したら完了
          return OrderStatus.COMPLETED;
        }
        return inpOrder.statusClassValue;
      default:
        return inpOrder.statusClassValue;
    }
  }

  // OrderDetail共通コンポーネントエリアの変更イベント
  function handleChangeHeader(e: OrderDetailChangeEvent) {
    // 定価フラグの更新を受け取る
    if (e.name === "regularPriceFlg") {
      setCurrentRegularPriceFlg(e.value as boolean);
      return;
    }
    // 得意先コード変更時の処理
    if (e.name === "companyCode") {
      setCurrentRegularPriceFlg(e.regularPriceFlg ?? false);
      // 定価フラグの変更を伴う得意先変更があり、明細がある場合は確認ダイアログを表示
      if (e.regularPriceFlgChanged) {
        const hasDetails = inpDetails.some((detail) => detail.productCode);
        if (hasDetails) {
          // 確認ダイアログを表示
          showDialog({
            id: "I082",
          });
        }
      }
    }

    setFormChanged(true);

    setInpOrder((prev) => ({ ...prev, [e.name]: e.value }));

    if (e.name === "dispatchFlg") {
      // 技術者派遣の場合
      if (e.value) {
        // トグルONになったら経費入力のためにスペーサー行を追加(BEMACユーザのみ)
        if (auth.user().bemacFlg && canAddExpensesRow(inpDetails)) {
          setInpDetails((prev) => {
            const rows = [...prev];
            // 最終行（空行のはず）にスペーサーをセット
            rows[rows.length - 1].productCode = ProductCodeConst.BLANK;
            // 空行を2つ追加
            addNewRow(rows, [""]);
            return rows;
          });
          //経費行を追加
          addExpensesRow(undefined, false);
        }
      }
    }
    if (e.name === "replyDiscountPrice" || e.name === "replyTax") {
      // 割引額または税率の場合、合計金額再計算
      calculateReplyPrice(inpDetails);
    }
  }

  // テキストボックス変更
  function handleChangeInput(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    setFormChanged(true);
    setInpOrder((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  // グリッドのセル値変更イベント
  const onCellValueChanged = (event: CellValueChangedEvent) => {
    setFormChanged(true);
    if (event.column.getColId() === "confirmFlg") {
      // 出荷ボタンの場合はスキップ
      return;
    }
    // 自身が最終行なら一行追加する
    if (event.rowIndex === inpDetails.length - 1 && canAddNewRow()) {
      setInpDetails((prev) => {
        const newRows = prev.concat();
        addNewRow(newRows);
        return newRows;
      });
    }
    // 品目コード変更時は商品単一取得API実行
    if (event.column.getColId() === "productCode") {
      const productCode = encodeURIComponent(event.value + "");
      if (productCode && productCode !== ProductCodeConst.BLANK) {
        const queryParams: string[] = [];
        queryParams.push(
          `onlyLcmCosts=${
            inpOrder.orderClassValue === OrderFactor.LCM_PARTS
              ? "true"
              : "false"
          }`
        );
        if (inpOrder.companyCode) {
          queryParams.push(`companyCode=${inpOrder.companyCode}`);
        }
        const query = queryParams.join("&");
        api
          .get<ProductGetResponse>(`/api/v1/products/${productCode}?${query}`)
          .then((response) => {
            setInpDetails((prev) => {
              const newRows = prev.map((it, index) => {
                if (index === event.rowIndex) {
                  const data = response.data;
                  const newRow = prev[event.rowIndex ?? 0];
                  newRow.productGroupName = data.productGroupName;
                  newRow.productGroupNameEn = data.productGroupNameEn;
                  newRow.spec = data.spec;
                  newRow.unitCost = data.unitPrice ?? undefined;
                  newRow.unit = data.unit;
                  newRow.note = data.note;
                  calculateRowSubTotal(newRow); // 小計計算
                  return newRow;
                } else {
                  return it;
                }
              });
              // 金額再計算
              calculateTempPrice(newRows);
              calculateReplyPrice(newRows);

              return newRows;
            });
          })
          .catch((error) => {
            if (error.response.status === 404) {
              showDialog({ id: "E029" });
            } else {
              showDialog({ error });
            }
          });
      } else {
        //品目コードクリア時、または---に変更時はレコードの値もクリアする
        setInpDetails((prev) => {
          const newRows = prev.map((it, index) => {
            if (index === event.rowIndex) {
              let newRow = {
                productCode: productCode ?? "",
                key: it.key,
                isDbRow: it.isDbRow,
                orderDetailNo: it.orderDetailNo,
                productGroupName: undefined,
                productGroupNameEn: undefined,
                spec: undefined,
                unit: undefined,
                note: undefined,
                quantity: undefined,
                unitCost: undefined,
                detailSubTotal: undefined,
                replyQuantity: undefined,
                replyUnitCost: undefined,
                replyDetailSubTotal: undefined,
                replyDeliveryDate: undefined,
                shippingQuantity: undefined,
                confirmFlg: false,
                confirmUserId: undefined,
                confirmDateTime: undefined,
                cancelOrderQuantity: undefined,
                cancelUserId: undefined,
                cancelDateTime: undefined,
                r3WbsLv2: undefined,
                r3OrderNo: undefined,
                r3ShippingDate: undefined,
                referenceDetailNo: undefined,
                cancelSubTotal: undefined,
                existFlg: true,
              };
              return newRow;
            } else {
              return it;
            }
          });
          // 金額再計算
          calculateTempPrice(newRows);
          calculateReplyPrice(newRows);
          return newRows;
        });
      }
    }

    // 数量、単価変更時は仮注文金額再計算
    if (
      event.column.getColId() === "quantity" ||
      event.column.getColId() === "unitCost"
    ) {
      setInpDetails((prev) => {
        const newRows = prev.concat();
        newRows.forEach((row) => {
          calculateRowSubTotal(row);
        });
        calculateTempPrice(newRows);
        return newRows;
      });
    }
    // BEMAC回答の数量、単価変更時は金額再計算
    if (
      event.column.getColId() === "replyQuantity" ||
      event.column.getColId() === "replyUnitCost"
    ) {
      setInpDetails((prev) => {
        const newRows = prev.concat();
        newRows.forEach((row) => {
          calculateRowSubTotal(row); // 小計計算
        });
        // 合計金額再計算
        calculateReplyPrice(newRows);
        return newRows;
      });
    }
    // キャンセル数、単価変更時はキャンセル金額計算
    if (
      event.column.getColId() === "cancelOrderQuantity" ||
      event.column.getColId() === "replyUnitCost"
    ) {
      setInpDetails((prev) => {
        const newRows = prev.concat();
        newRows.forEach((row) => {
          calculateRowSubTotal(row); // 小計計算
        });
        return newRows;
      });
    }
  };

  // 行の中の小計を計算
  function calculateRowSubTotal(row: OrderDetailFormRowValues) {
    // 注文明細小計
    if (row.unitCost === undefined || row.quantity === undefined) {
      row.detailSubTotal = undefined;
    } else {
      row.detailSubTotal = (row.unitCost ?? 0) * (row.quantity ?? 0);
    }
    // 注文回答明細小計
    if (row.replyUnitCost === undefined || row.replyQuantity === undefined) {
      row.replyDetailSubTotal = undefined;
    } else {
      row.replyDetailSubTotal =
        (row.replyUnitCost ?? 0) * (row.replyQuantity ?? 0);
    }
    // キャンセル数量
    if (
      row.replyUnitCost === undefined ||
      row.cancelOrderQuantity === undefined
    ) {
      row.cancelSubTotal = undefined;
    } else {
      row.cancelSubTotal =
        (row.replyUnitCost ?? 0) * (row.cancelOrderQuantity ?? 0);
    }
  }

  // 新規行追加
  function addNewRow(
    rows: OrderDetailFormRowValues[],
    productCodes?: string[]
  ) {
    const getMaxKey = (rows: OrderDetailFormRowValues[]) => {
      if (rows.length > 0) {
        return Math.max(...rows.map((it) => it.key));
      }
      return 0;
    };
    // 行を追加(複数も可)
    let maxKey = getMaxKey(rows);
    productCodes?.forEach((productCode) => {
      let newRow = {
        key: maxKey + 1,
        orderDetailNo: rows.length + 1,
        isDbRow: false,
        productCode: productCode,
        productGroupName: undefined,
        productGroupNameEn: undefined,
        spec: undefined,
        unit: undefined,
        note: undefined,
        quantity: undefined,
        unitCost: undefined,
        detailSubTotal: undefined,
        replyQuantity: undefined,
        replyUnitCost: undefined,
        replyDetailSubTotal: undefined,
        replyDeliveryDate: undefined,
        shippingQuantity: undefined,
        confirmFlg: false,
        confirmUserId: undefined,
        confirmDateTime: undefined,
        cancelOrderQuantity: undefined,
        cancelUserId: undefined,
        cancelDateTime: undefined,
        r3WbsLv2: undefined,
        r3OrderNo: undefined,
        r3ShippingDate: undefined,
        referenceDetailNo: undefined,
        cancelSubTotal: undefined,
        existFlg: true,
      };
      rows.push(newRow);
      maxKey++;
    });

    // 最終行を追加
    const blankRow = {
      key: maxKey + 1,
      orderDetailNo: rows.length + 1,
      isDbRow: false,
      productCode: "",
      productGroupName: undefined,
      productGroupNameEn: undefined,
      spec: undefined,
      unit: undefined,
      note: undefined,
      quantity: undefined,
      unitCost: undefined,
      detailSubTotal: undefined,
      replyUnitCost: undefined,
      replyQuantity: undefined,
      replyDetailSubTotal: undefined,
      replyDeliveryDate: undefined,
      shippingQuantity: undefined,
      confirmFlg: false,
      confirmUserId: undefined,
      confirmDateTime: undefined,
      cancelOrderQuantity: undefined,
      cancelUserId: undefined,
      cancelDateTime: undefined,
      r3WbsLv2: undefined,
      r3OrderNo: undefined,
      r3ShippingDate: undefined,
      referenceDetailNo: undefined,
      cancelSubTotal: undefined,
      existFlg: true,
    };
    rows.push(blankRow);
  }

  //経費行を追加する
  function addExpensesRow(
    details: OrderDetailFormRowValues[] | undefined, //初期値だけ設定したい場合にのみ指定する
    initialFlg: boolean
  ) {
    //経費
    const productCode = ProductCodeConst.EXPENSES;

    //商品単一取得API実行
    api
      .get<ProductGetResponse>(`/api/v1/products/${productCode}`)
      .then((response) => {
        if (!details) {
          setInpDetails((prev) => {
            const newRows = prev.map((it, index) => {
              if (index === prev.length - 2) {
                const data = response.data;
                const newRow = prev[prev.length ? prev.length - 2 : 0];
                createExpensesRow(newRow, data);
                return newRow;
              } else {
                return it;
              }
            });
            // 金額再計算
            calculateTempPrice(newRows);
            calculateReplyPrice(newRows);
            if (initialFlg) {
              const copiedDetails = newRows.map((it) => {
                return { ...it };
              });
              setInitialDetails(copiedDetails);
            }

            return newRows;
          });
        } else {
          // detailsは初期データのみを変更したい場合にのみ設定する
          const newRows = details.map((it, index) => {
            if (index === details.length - 2) {
              const data = response.data;
              const newRow = details[details.length ? details.length - 2 : 0];
              createExpensesRow(newRow, data);
              return newRow;
            } else {
              return it;
            }
          });
          const copiedDetails = newRows.map((it) => {
            return { ...it };
          });
          setInitialDetails(copiedDetails);
        }
      })
      .catch((error) => {
        if (error.response.status === 404) {
          showDialog({ id: "E029" });
        } else {
          showDialog({ error });
        }
      });
  }

  //経費行オブジェクト編集
  function createExpensesRow(
    newRow: OrderDetailFormRowValues,
    data: ProductGetResponse
  ) {
    newRow.productCode = data.productCode;
    newRow.productGroupName = data.productGroupName;
    newRow.productGroupNameEn = data.productGroupNameEn;
    newRow.spec = data.spec;
    newRow.unitCost = data.unitPrice ?? undefined;
    newRow.unit = data.unit;
    newRow.note = data.note;
    newRow.detailSubTotal = (data.unitPrice ?? 0) * (newRow.quantity ?? 0);
    newRow.replyQuantity = 1;
    newRow.replyUnitCost = 0;
    calculateRowSubTotal(newRow); // 小計計算
  }

  // 合計金額再計算
  function calculateReplyPrice(gridRows: OrderDetailFormRowValues[]) {
    setInpOrder((prev) => {
      const price = calculateReplyPriceCore(
        gridRows,
        prev.replyDiscountPrice,
        prev.replyTax
      );

      return {
        ...prev,
        replySubtotalPrice: price.subtotalPrice,
        replyTaxPrice: price.taxPrice,
        replyTotalPrice: price.totalPrice,
      };
    });
  }
  /** キャンセル金額取得 */
  function getCancelTotalPrice() {
    let sum = 0;
    inpDetails.forEach((row) => {
      if (row.cancelSubTotal) {
        sum += row.cancelSubTotal;
      }
    });
    sum = sum + calculateTax(sum, inpOrder.replyTax); // 税込みにする
    return sum;
  }

  /** キャンセル後総計取得 */
  function getTotalPriceWithCancle() {
    let sum = 0;
    if (inpOrder.replyTotalPrice) {
      sum = inpOrder.replyTotalPrice - getCancelTotalPrice();
    }
    return sum;
  }

  // 商品一覧で部品を選択
  function handleSelectProducts(products: ProductsGetResponse[]) {
    setFormChanged(true);
    // 最終行の新規行を除いてリストを再生成
    const rows = inpDetails.concat();
    rows.pop();
    // 選択された部品を追加
    let nextKey = Math.max(...inpDetails.map((it) => it.key)) + 1;
    let nextNo = rows.length + 1;

    const vesselProductDetailNoList = inpDetails
      .filter((it) => it.referenceDetailNo)
      .map((row) => {
        return row.referenceDetailNo;
      });

    products.forEach((it) => {
      // LCMの場合部品交換リストからすでに選択済みの商品の場合除外
      if (
        inpOrder.orderClassValue === OrderFactor.LCM_PARTS &&
        vesselProductDetailNoList?.includes(it.detailNo)
      ) {
        return;
      }
      const newRow: OrderDetailFormRowValues = {
        key: nextKey++,
        isDbRow: false,
        orderDetailNo: nextNo++,
        productCode: it.productCode,
        productGroupName: it.productGroupName,
        productGroupNameEn: it.productGroupNameEn,
        spec: it.spec,
        unit: it.unit,
        note: it.note,
        quantity: auth.user().bemacFlg ? undefined : 1,
        unitCost: it.unitPrice,
        detailSubTotal: undefined,
        replyQuantity: auth.user().bemacFlg ? 1 : undefined,
        replyUnitCost: auth.user().bemacFlg ? it.unitPrice : undefined,
        replyDetailSubTotal: undefined,
        replyDeliveryDate: undefined,
        shippingQuantity: undefined,
        confirmFlg: false,
        confirmUserId: undefined,
        confirmDateTime: undefined,
        cancelOrderQuantity: undefined,
        cancelUserId: undefined,
        cancelDateTime: undefined,
        r3WbsLv2: undefined,
        r3OrderNo: undefined,
        r3ShippingDate: undefined,
        referenceDetailNo: it.detailNo,
        cancelSubTotal: undefined,
        existFlg: true,
      };
      calculateRowSubTotal(newRow); // 小計を計算
      // 追加
      rows.push(newRow);
    });
    // 新規行を追加
    addNewRow(rows);
    // stateに保存
    setInpDetails(rows);
    // 金額再計算
    calculateTempPrice(rows);
    calculateReplyPrice(rows);
  }

  // 注文書PDFボタンクリック
  function handleClickOutputPdf() {
    // 保存してなかったらエラー
    if (!orderNo || formChanged) {
      showDialog({
        id: "E084",
        args: [tc("注文")],
      });
      return;
    }

    api
      .get(`/api/v1/order-pdf/${orderNo}`, {
        responseType: "blob",
      })
      .then((it) => {
        const pdf = new Blob([it.data], { type: "application/pdf" });
        const pdfURL = URL.createObjectURL(pdf);
        window.open(pdfURL);
      })
      .catch((error) => {
        showDialog({ error });
      });
  }

  // 完工見積書PDFボタンクリック
  function handleClickOutputCompQuotationPdf() {
    // 保存してなかったらエラー
    if (!orderNo || formChanged) {
      showDialog({
        id: "E084",
        args: [tc("注文")],
      });
      return;
    }

    api
      .get(`/api/v1/comp-quotation-pdf/${orderNo}`, {
        responseType: "blob",
      })
      .then((it) => {
        const pdf = new Blob([it.data], { type: "application/pdf" });
        const pdfURL = URL.createObjectURL(pdf);
        window.open(pdfURL);
      })
      .catch((error) => {
        showDialog({ error });
      });
  }

  // 注文承認チェック（初期状態から変更がないか）
  function validateApprove() {
    const isChanged = (
      initial: string | number | boolean | undefined,
      current: string | number | boolean | undefined
    ) => {
      if (!initial && !current) {
        // ともに未入力
        return false;
      }
      if ((initial && !current) || (!initial && current)) {
        // 片方が未入力
        return true;
      }

      if (initial && current) {
        if (initial === current) {
          return false;
        } else {
          return true;
        }
      }
    };

    // 変更チェックする項目
    const names = [
      "dispatchFlg",
      "dispatchPlace",
      "dispatchDate",
      "replyInstallmentDeliveryFlg",
      "replyDiscountPrice",
      "replyTax",
    ];

    // 項目ごとループ
    for (const name of names) {
      // 項目名で値を取り出せるようにする
      const key = name as keyof OrderDetailFormValues;

      const initialValue = initialData ? initialData[key] : undefined;
      const currentValue = inpOrder[key];

      if (isChanged(initialValue, currentValue)) {
        return false;
      }
    }

    // 明細部のチェック
    const namesOfDetail = [
      "productCode",
      "spec",
      "quantity",
      "replyQuantity",
      "replyUnitCost",
      "replyDeliveryDate",
    ];
    if (inpDetails.length !== initialDetails.length) {
      // 明細数が異なる
      return false;
    }
    // 行ごとループ
    for (const index in inpDetails) {
      const initialRow = initialDetails[index];
      const currentRow = inpDetails[index];
      // 項目ごとループ
      for (const name of namesOfDetail) {
        // 項目名で値を取り出せるようにする
        const key = name as keyof OrderDetailFormRowValues;

        const initialValue = initialRow[key];
        const currentValue = currentRow[key];

        if (isChanged(initialValue, currentValue)) {
          return false;
        }
      }
    }
    return true;
  }

  // 仮注文金額再計算
  function calculateTempPrice(gridRows: OrderDetailFormRowValues[]) {
    let subtotal = 0;
    gridRows.forEach((row) => {
      subtotal += row.detailSubTotal ?? 0;
    });
    setTempSubtotalPrice(subtotal);
  }

  // 割引計算ボタン押下時の計算
  function handleClickCalculation() {
    const discountRate = inpOrder.discountRate || 0;

    // 小計を取得
    let subtotal = 0;
    inpDetails.forEach((row) => {
      subtotal += row.replyDetailSubTotal ?? 0;
    });

    // 割引額を計算: 小計 × (割引率 / 100)
    const discountAmount = subtotal * (discountRate / 100);

    // 割引額を設定して金額再計算
    setInpOrder((prev) => {
      const updatedCalculation = {
        ...prev,
        replyDiscountPrice: Math.floor(discountAmount), // 小数部切り捨て
      };

      // 合計金額再計算
      const subtotalMinusDiscount = subtotal - Math.floor(discountAmount);
      const taxPrice = calculateTax(subtotalMinusDiscount, prev.replyTax);
      const totalPrice = subtotalMinusDiscount + taxPrice;

      return {
        ...updatedCalculation,
        replySubtotalPrice: subtotal,
        replyTaxPrice: taxPrice,
        replyTotalPrice: totalPrice,
      };
    });
  }

  // 行ドラッグ
  const { onRowDragEnter, onRowDragEnd } =
    useDetailRowDrag<OrderDetailFormRowValues>({
      gridRef,
      detailNoKey: "orderDetailNo",
      canAddNewRow,
      addNewRow,
      setRows: setInpDetails,
      setFormChanged,
    });

  // 行削除
  function onClickDeleteButton(key: number) {
    setFormChanged(true);
    // 自身の行を削除し、その他の行のNoを採番しなおす
    const newRows = inpDetails
      .filter((it) => it.key !== key)
      .map((it, itIndex) => {
        it.orderDetailNo = itIndex + 1;
        return it;
      });
    setInpDetails(newRows);
    // 金額再計算
    calculateTempPrice(newRows);
    calculateReplyPrice(newRows);
  }

  // グリッドの列定義
  const columnDefs = buildOrderDetailColumnDefs({
    tc,
    permission,
    readOnly,
    bemacFlg: auth.user().bemacFlg,
    inpOrder,
    shippingAllFlg,
    setShippingAllFlg,
    gridRef,
    onClickDeleteButton,
  });

  // モーダルbody部レンダリング
  function modalBody() {
    return (
      <>
        <OrderDetailHeader
          readOnly={readOnly || inpOrder.isChangeOrder}
          type={OrderDetailType.Order}
          status={inpOrder.statusClassValue}
          orderFactor={inpOrder.orderClassValue ?? ""}
          quotationNo={inpOrder.quotationNo}
          quotationDateTime={inpOrder.quotationDateTime ?? ""}
          quotationCheckUserId={inpOrder.quotationCheckUserId ?? undefined}
          quotationCheckUserName={inpOrder.quotationCheckUserName ?? ""}
          orderNo={orderNo}
          orderDateTime={inpOrder.orderDateTime}
          replyUserId={inpOrder.replyUserId ?? undefined}
          replyUserName={inpOrder.replyUserName ?? ""}
          companyCode={inpOrder.companyCode}
          companyName={inpOrder.companyName ?? ""}
          orderUserId={inpOrder.orderUserId}
          orderUserName={inpOrder.orderUserName}
          vesselName={inpOrder.vesselName ?? ""}
          imoNo={inpOrder.imoNo ?? ""}
          vesselBuilder={inpOrder.vesselBuilder ?? ""}
          yardNo={inpOrder.yardNo ?? ""}
          customerOrderNo1={inpOrder.customerOrderNo1 ?? ""}
          customerOrderNo2={inpOrder.customerOrderNo2 ?? ""}
          requestDeliveryDate={inpOrder.requestDeliveryDate ?? ""}
          requestReplyDeadline={inpOrder.requestReplyDeadline ?? ""}
          dispatchFlg={inpOrder.dispatchFlg}
          dispatchPlace={inpOrder.dispatchPlace ?? ""}
          dispatchDate={inpOrder.dispatchDate ?? ""}
          replyInstallmentDeliveryFlg={inpOrder.replyInstallmentDeliveryFlg}
          dockName={inpOrder.dockName ?? ""}
          dockScheduleStart={inpOrder.dockScheduleStart ?? ""}
          dockScheduleEnd={inpOrder.dockScheduleEnd ?? ""}
          problemProduct={inpOrder.problemProduct ?? ""}
          problemDetail={inpOrder.problemDetail ?? ""}
          onChange={handleChangeHeader}
          reQuotationFlg={inpOrder.isChangeOrder}
          compQuotationFlg={inpOrder.compQuotationFlg}
          isCompTempSave={inpOrder.isCompTempSave}
          fromQuotationDetail={props.fromQuotationDetail}
          customerQuotationNo1={inpOrder.customerQuotationNo1 ?? ""}
          customerQuotationNo2={inpOrder.customerQuotationNo2 ?? ""}
          editableFileUpload={editableFileUpload()}
          quotationOrderHistoryFlg={props.quotationOrderHistoryFlg}
        >
          <div
            className={`d-flex flex-wrap h-100 ${
              showsR3Values() ? "align-content-between" : "align-content-end"
            }`}
          >
            {showsR3Values() && (
              <div className={areaClass} data-cy="R3エリア">
                <div className="input-group">
                  <span className="b-input-group-text" style={itemTitleStyle}>
                    PJ番号
                  </span>
                  <input
                    type="text"
                    name="r3Wbs"
                    className="form-control"
                    disabled={!editableR3Values()}
                    defaultValue={inpOrder.r3Wbs}
                    maxLength={15}
                    onChange={handleChangeInput}
                    data-cy="WBSテキスト"
                  />
                </div>
                <div className="input-group">
                  <span className="b-input-group-text" style={itemTitleStyle}>
                    出荷日
                  </span>
                  <input
                    type="date"
                    name="r3ShippingDate"
                    className="form-control"
                    disabled={!editableR3Values()}
                    defaultValue={formatDate(
                      inpOrder.r3ShippingDate,
                      "YYYY-MM-DD"
                    )}
                    onChange={handleChangeInput}
                    data-cy="R3出荷日テキスト"
                  />
                </div>
                <div className="input-group">
                  <span
                    className="b-input-group-text"
                    style={{ width: "11rem", height: "115px" }}
                  >
                    コメント
                  </span>
                  <textarea
                    name="r3Comment"
                    className="form-control textarea"
                    disabled={!editableR3Values()}
                    defaultValue={inpOrder.r3Comment}
                    maxLength={20}
                    onChange={handleChangeInput}
                    data-cy="R3コメントテキスト"
                  />
                </div>
              </div>
            )}
            {visibleReplyDeliveryDate() && (
              <div className={areaClass}>
                <div className="input-group">
                  <span className="b-input-group-text" style={itemTitleStyle}>
                    {tc("出荷納期")}
                  </span>
                  <input
                    name="replyDeliveryDate"
                    type="date"
                    className="form-control"
                    disabled={!editableReplyDeliveryDate()}
                    value={formatDate(inpOrder.replyDeliveryDate, "YYYY-MM-DD")}
                    onChange={handleChangeInput}
                    data-cy="出荷納期テキスト"
                    style={{ width: "235px" }}
                  />
                </div>
                {inpOrder.replyInstallmentDeliveryFlg &&
                  editableReplyDeliveryDate() && (
                    <div>
                      <span
                        className="ms-1"
                        style={{ color: "red" }}
                        data-cy="出荷納期注記"
                      >
                        {tc("※分納/後送があるため、明細に入力してください。")}
                      </span>
                    </div>
                  )}
              </div>
            )}
          </div>
        </OrderDetailHeader>
        <div>
          {/* 明細 */}
          <div
            className="ag-theme-alpine w-100 mx-auto b-detail-items b-grid-outer b-header-row-white"
            data-cy="明細グリッド"
          >
            <AgGridReact
              domLayout="autoHeight"
              defaultColDef={defaultColDef}
              columnDefs={columnDefs}
              suppressRowClickSelection={true}
              rowData={inpDetails}
              onCellValueChanged={onCellValueChanged}
              singleClickEdit
              ref={gridRef}
              rowDragManaged={true}
              onRowDragEnter={onRowDragEnter}
              onRowDragEnd={onRowDragEnd}
              stopEditingWhenCellsLoseFocus={true}
            />
            {/* 部品検索ボタン */}
            <div className="row">
              <div className="col-4">
                {auth.user().bemacFlg && (
                  <MdOutlineContentCopy
                    size="30px"
                    onClick={() => copyToClipboard()}
                    cursor="pointer"
                    style={{ marginTop: "30px" }}
                  />
                )}
              </div>
              <div className="col-4">
                <div className="text-center">
                  {canAddNewRow() && (
                    <input
                      type="button"
                      className="btn b-btn-primary"
                      value={tc("部品検索")}
                      data-cy="部品検索ボタン"
                      onClick={(e) => productListModalRef.current?.show()}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 金額 */}
          <div className="row mt-2">
            <div className="col-4">
              {!isGeneralEngineer() && (
                <OrderDetailPrice
                  readOnly={readOnly || inpOrder.isChangeOrder}
                  type={OrderDetailType.Order}
                  status={inpOrder.statusClassValue}
                  replySubtotalPrice={inpOrder.replySubtotalPrice}
                  replyDiscountPrice={inpOrder.replyDiscountPrice}
                  replyTax={inpOrder.replyTax}
                  replyTaxPrice={inpOrder.replyTaxPrice}
                  replyTotalPrice={inpOrder.replyTotalPrice}
                  onChange={handleChangeHeader}
                  reQuotationFlg={inpOrder.isChangeOrder}
                  showTempPrice={showsTempPrice()}
                  tempSubtotalPrice={tempSubtotalPrice}
                />
              )}
            </div>
            {/* 割引率入力欄と計算ボタン */}
            <div
              className="col-4"
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "35px",
              }}
            >
              {!isGeneralEngineer() &&
                auth.user().bemacFlg &&
                currentRegularPriceFlg && (
                  <OrderDetailDiscountRate
                    readOnly={readOnly || inpOrder.isChangeOrder}
                    type={OrderDetailType.Order}
                    status={inpOrder.statusClassValue}
                    discountRate={inpOrder.discountRate}
                    onChange={handleChangeHeader}
                    reQuotationFlg={inpOrder.isChangeOrder}
                    onCalculateDiscount={handleClickCalculation}
                  />
                )}
              {/* ここから　キャンセル金額 */}
              {showsShippingColumns() &&
                !isGeneralEngineer() &&
                (auth.user().bemacFlg || getCancelTotalPrice() > 0) && (
                  <div
                    className="p-1 bg-white b-radius"
                    style={{ marginTop: "152px" }}
                    data-cy="キャンセル金額エリア"
                  >
                    <div className="input-group">
                      <span
                        className="b-input-group-text"
                        style={{ width: "10rem" }}
                      >
                        {tc("キャンセル金額")}
                      </span>
                      <span
                        className="form-control b-input-group-value-text-end"
                        style={{ color: "red" }}
                        data-cy="キャンセル金額"
                      >
                        {getCancelTotalPrice().toLocaleString()}
                      </span>
                    </div>
                    <div className="input-group">
                      <span
                        className="b-input-group-text"
                        style={{ width: "10rem" }}
                      >
                        {tc("キャンセル後総計")}
                      </span>
                      <span
                        className="form-control b-input-group-value-text-end"
                        data-cy="キャンセル後総計"
                      >
                        {getTotalPriceWithCancle().toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              {/* ここまで　キャンセル金額 */}
            </div>
            <div className="col-4">
              <OrderDetailDelivery
                readOnly={readOnly || inpOrder.isChangeOrder}
                type={OrderDetailType.Order}
                status={inpOrder.statusClassValue}
                deliveryName={inpOrder.deliveryName}
                deliveryPostalCode={inpOrder.deliveryPostalCode}
                deliveryAddress={inpOrder.deliveryAddress}
                deliveryTel={inpOrder.deliveryTel}
                reQuotationFlg={inpOrder.isChangeOrder}
                onChange={handleChangeHeader}
              />
            </div>
          </div>
          {/* ファイル添付（見積・注文含む） */}
          <div className="row">
            <div className="col-6">
              <div className={`${areaClass} pt-2`} data-cy="お客様添付ファイル">
                <div className="pb-1">
                  <span
                    className="b-input-group-text"
                    style={{ display: "inline" }}
                  >
                    {tc("お客様 添付ファイル")}
                  </span>
                  {editableFileUpload() && (
                    <span className="ms-2" style={{ color: "red" }}>
                      {tc("※ファイル添付は、最大10件までです。")}
                    </span>
                  )}
                </div>
                <S3Uploader
                  editable={editableFileUpload()}
                  maxCount={10}
                  ref={uploaderCustomerRef}
                />
              </div>
            </div>
            <div className="col-6">
              <div className={`${areaClass} pt-2`} data-cy="BEMAC添付ファイル">
                <div className="pb-1">
                  <span
                    className="b-input-group-text-light_blue"
                    style={{ display: "inline" }}
                  >
                    {tc("BEMAC 添付ファイル")}
                  </span>
                  {editableShippingFileUpload() && (
                    <span className="ms-2" style={{ color: "red" }}>
                      {tc("※ファイル添付は、最大10件までです。")}
                    </span>
                  )}
                </div>
                <S3Uploader
                  editable={editableShippingFileUpload()}
                  maxCount={10}
                  ref={uploaderBemacRef}
                />
              </div>
            </div>
          </div>

          {/* 備考、コメント */}
          <div className="row">
            <div className="col-6">
              <div className={areaClass}>
                <div>
                  <div
                    className="b-input-group-text"
                    style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                  >
                    {tc("見積時備考")}
                  </div>
                  <textarea
                    className={textAreaClass}
                    disabled
                    defaultValue={inpOrder.quotationComment}
                    data-cy="見積時備考テキスト"
                  />
                </div>
                <div className="mt-3">
                  {showsUserCommentCancel() && (
                    <span className="mt-2" style={{ color: "red" }}>
                      {tc(
                        "※注文取消をされる際は、理由を備考にご記入ください。"
                      )}
                    </span>
                  )}
                  {showsUserCommentDeniedChange() && (
                    <span className="mt-2" style={{ color: "red" }}>
                      {tc(
                        "※注文停止をされる際は、理由を備考にご記入ください。"
                      )}
                    </span>
                  )}
                  <div
                    className="b-input-group-text"
                    style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                  >
                    {tc("備考")}
                  </div>
                  <textarea
                    name="orderComment"
                    className={textAreaClass}
                    disabled={!editableUserComment()}
                    value={inpOrder.orderComment}
                    maxLength={500}
                    onChange={handleChangeInput}
                    data-cy="お客様備考テキスト"
                  />
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className={areaClass}>
                <div>
                  <div
                    className="b-input-group-text-light_blue"
                    style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                  >
                    {tc("注文回答コメント")}
                  </div>
                  <textarea
                    name="replyComment"
                    className={textAreaClass}
                    disabled={!editableBemacReplyComment()}
                    value={inpOrder.replyComment}
                    maxLength={500}
                    onChange={handleChangeInput}
                    data-cy="注文回答コメントテキスト"
                  />
                </div>
                {(showsShippingColumns() ||
                  (OrderStatus.REQUESTED_CANCEL === inpOrder.statusClassValue &&
                    inpOrder.replyDateTime)) && (
                  <div className="mt-3">
                    <div
                      className="b-input-group-text-light_blue"
                      style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                    >
                      {tc("出荷完了コメント")}
                    </div>
                    <textarea
                      name="shippedComment"
                      className={textAreaClass}
                      disabled={!editableBemacShippingComment()}
                      value={inpOrder.shippedComment}
                      maxLength={500}
                      onChange={handleChangeInput}
                      data-cy="出荷完了コメントテキスト"
                    />
                  </div>
                )}
                {auth.user().bemacFlg && (
                  <div className="mt-3">
                    <div
                      className="b-input-group-text-light_blue"
                      style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
                    >
                      {tc("フリーメモ")}
                    </div>
                    <textarea
                      name="bemacFreeComment"
                      className={textAreaClass}
                      disabled={!editableBemacFreeComment()}
                      value={inpOrder.bemacFreeComment}
                      maxLength={400}
                      onChange={handleChangeInput}
                      data-cy="フリーメモテキスト"
                    />
                  </div>
                )}
                {auth.user().bemacFlg && (
                  <div
                    className={`${areaClass} pt-2`}
                    data-cy="BEMACフリーメモ添付ファイル"
                  >
                    <div className="pb-1">
                      <span
                        className="b-input-group-text-light_blue"
                        style={{ display: "inline" }}
                      >
                        {tc("BEMAC 添付ファイル")}
                      </span>
                      {editableBemacFreeComment() && (
                        <span className="ms-2" style={{ color: "red" }}>
                          {tc("※ファイル添付は、最大10件までです。")}
                        </span>
                      )}
                    </div>
                    <S3Uploader
                      editable={editableBemacFreeComment()}
                      maxCount={10}
                      ref={uploaderFreeMemo}
                    />
                  </div>
                )}
              </div>
            </div>
            {(showsShippingColumns() ||
              (OrderStatus.ORDERED &&
                !props.fromQuotationDetail &&
                inpOrder.statusClassValue !== OrderStatus.DRAFT)) && (
              <div className="row">
                <div className="col-12 px-0" style={{ marginLeft: "12px" }}>
                  <div
                    className={`${areaClass} pt-2`}
                    data-cy="送り状など添付ファイル"
                  >
                    <div className="pb-1">
                      <span
                        className="b-input-group-text-light_blue"
                        style={{ display: "inline" }}
                      >
                        {tc("送り状など添付")}
                      </span>
                      {editableShippingFileUpload() && (
                        <span className="ms-2" style={{ color: "red" }}>
                          {tc("※送り状など添付は、最大5件までです。")}
                        </span>
                      )}
                    </div>
                    {(inpOrder.statusClassValue === OrderStatus.ORDERED ||
                      inpOrder.statusClassValue === OrderStatus.ARRANGING ||
                      inpOrder.statusClassValue === OrderStatus.COMPLETED) && (
                      <S3Uploader
                        editable={editableShippingFileUpload()}
                        maxCount={5}
                        ref={uploaderShippingRef}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <ProductListModal
          onDecide={handleSelectProducts}
          onlyLcmCosts={inpOrder.orderClassValue === OrderFactor.LCM_PARTS}
          imoNo={
            inpOrder.orderClassValue === OrderFactor.LCM_PARTS
              ? inpOrder.imoNo
              : undefined
          }
          ref={productListModalRef}
          companyCode={inpOrder.companyCode}
        />
      </>
    );
  }

  // レンダリング
  return (
    <Modal
      fullscreen={true}
      show={show}
      onShow={handleShow}
      onHide={handleClose}
      scrollable
      data-cy="注文詳細モーダル"
    >
      <Modal.Header closeButton>
        <Modal.Title>{tc("注文詳細")}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="b-modal-detail"
        style={{ padding: "0px 12px 0px 12px" }}
      >
        {modalBody()}
      </Modal.Body>
      <Modal.Footer className="b-modal-detail">
        <OrderDetailFooter
          fromQuotationDetail={props.fromQuotationDetail}
          readOnly={readOnly}
          inpOrder={inpOrder}
          onClickAction={handleClickActionButton}
          onClickOutputPdf={handleClickOutputPdf}
          onClickOutputCompQuotationPdf={handleClickOutputCompQuotationPdf}
          onClose={handleClose}
        />
      </Modal.Footer>
    </Modal>
  );
});

export default OrderDetail;
